import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent } from '../../../components/ui/card';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { Loader2, Save, ArrowLeft, Image, X, LinkIcon, Building, Plus } from 'lucide-react';
import { Partner, addPartner, updatePartner } from '../../../services/partnerService';
import { MediaSelector } from '../../../components/media/MediaSelector';
import { useTenantContext } from '../../../context/TenantContext';
import { applyDomainFilterToQuery } from '../../../hooks/useDomainQuery';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"];
const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];

const partnerSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().optional(),
  website_url: z.string().url('URL invalide').optional().or(z.literal('')),
  project_type: z.string().optional(),
  logo_url: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp, .svg'),
});

type PartnerFormValues = z.infer<typeof partnerSchema>;

export const PartnerEdit = () => {
  useCheckPermissions(['admin']);
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isNewPartner = id === 'new';
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const { tenant } = useTenantContext();

  // Faire entièrement confiance à la résolution du domaine depuis le contexte
  const domain = typeof window !== 'undefined' ? window.location.hostname : null;
  const tenantId = tenant?.id || '2';

  // 🔍 DEBUG - Vérifier les valeurs utilisées
  console.log('🔍 PartnerEdit - tenant:', tenant);
  console.log('🔍 PartnerEdit - domain:', domain);
  console.log('🔍 PartnerEdit - tenantId:', tenantId);

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerSchema),
    defaultValues: {
      name: '',
      description: '',
      website_url: '',
      project_type: '',
    },
  });

  useEffect(() => {
    if (!isNewPartner) {
      fetchPartner();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchPartner = async () => {
    try {
      // Construire la requête avec filtrage domaine
      let query = supabase
        .from('partners')
        .select('*')
        .eq('id', id);

      // Appliquer le filtre domaine si nécessaire
      if (domain) {
        query = applyDomainFilterToQuery(query);
      }

      const { data: partner, error } = await query.single();

      if (error) throw error;

      if (partner) {
        setCurrentPartner(partner);
        form.reset({
          name: partner.name || '',
          description: partner.description || '',
          website_url: partner.website_url || '',
          project_type: partner.project_type || '',
        });

        if (partner.logo_url) {
          setImagePreview(partner.logo_url);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du partenaire:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les informations du partenaire',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let files: FileList | null = null;
    
    if ('dataTransfer' in e) {
      // Gestion du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        files = e.dataTransfer.files;
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Gestion du clic sur l'input file
      files = e.target.files;
    }
    
    if (files) {
      // Mode multiple : créer plusieurs partenaires
      if (files.length > 1) {
        handleMultipleImagesUpload(files);
        return;
      }
      
      // Mode simple : un seul fichier
      const file = files[0];
      
      // Vérification du type de fichier
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_IMAGE_EXTENSIONS.includes(fileExtension || '')) {
        toast({
          title: 'Format non supporté',
          description: 'Veuillez utiliser un fichier image (JPEG, JPG, PNG, WEBP ou SVG)',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérification de la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Fichier trop volumineux',
          description: 'La taille maximale autorisée est de 5 Mo',
          variant: 'destructive',
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        // Création d'un DataTransfer pour simuler un FileList
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file as File);
        // Mise à jour de la valeur du champ de formulaire
        form.setValue('logo_url', dataTransfer.files, { shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Fonction pour ouvrir le sélecteur de média
  const openMediaSelector = () => {
    setShowMediaSelector(true);
  };

  // Fonction pour gérer l'upload multiple d'images
  const handleMultipleImagesUpload = async (files: FileList) => {
    const validFiles: File[] = [];
    
    // Valider tous les fichiers
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!ACCEPTED_IMAGE_EXTENSIONS.includes(fileExtension || '')) {
        toast({
          title: 'Format non supporté',
          description: `${file.name}: Format non supporté`,
          variant: 'destructive',
        });
        continue;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Fichier trop volumineux',
          description: `${file.name}: Taille maximale 5 Mo`,
          variant: 'destructive',
        });
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    // Confirmer la création multiple
    if (!window.confirm(`Créer ${validFiles.length} partenaire(s) à partir des images sélectionnées ?`)) {
      return;
    }
    
    setSaving(true);
    
    try {
      let successCount = 0;
      
      for (const file of validFiles) {
        try {
          // Générer un ID temporaire pour le partenaire
          const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2)}`;
          const fileExt = file.name.split('.').pop();
          const fileName = `partner_${tempId}_${Date.now()}.${fileExt}`;
          const filePath = `tenant/${tenantId}/${fileName}`;
          
          // Upload du fichier
          const { error: uploadError } = await supabase.storage
            .from('media')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true
            });
          
          if (uploadError) throw uploadError;
          
          // Obtenir l'URL publique
          const { data: publicUrlData } = supabase.storage
            .from('media')
            .getPublicUrl(filePath);
          
          // Créer le partenaire avec le nom du fichier (sans extension)
          const partnerName = file.name.replace(/\.[^/.]+$/, '');
          
          const { error: createError } = await supabase
            .from('partners')
            .insert({
              name: partnerName,
              description: '',
              logo_url: publicUrlData.publicUrl,
              tenant_id: tenantId,
              domain: domain,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          
          if (createError) throw createError;
          
          successCount++;
        } catch (error) {
          console.error(`Erreur pour ${file.name}:`, error);
        }
      }
      
      toast({
        title: 'Partenaires créés',
        description: `${successCount} partenaire(s) créé(s) avec succès sur ${validFiles.length}`,
      });
      
      // Rediriger vers la liste
      navigate('/admin/partners');
    } catch (error) {
      console.error('Erreur lors de la création multiple:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la création des partenaires.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Fonction pour gérer la sélection multiple depuis la médiathèque
  const handleMultipleMediaSelection = async (urls: string | string[]) => {
    // Mode simple - une seule URL
    if (typeof urls === 'string') {
      setImagePreview(urls);
      setShowMediaSelector(false);
      return;
    }
    
    // Si c'est un tableau vide, ne rien faire
    if (urls.length === 0) {
      return;
    }
    
    // Mode multiple
    if (!window.confirm(`Créer ${urls.length} partenaire(s) à partir des images sélectionnées ?`)) {
      return;
    }
    
    setSaving(true);
    setShowMediaSelector(false);
    
    try {
      let successCount = 0;
      
      for (const url of urls) {
        try {
          // Extraire le nom du fichier depuis l'URL
          const fileName = url.split('/').pop()?.split('?')[0] || 'Partenaire';
          const partnerName = decodeURIComponent(fileName).replace(/\.[^/.]+$/, '');
          
          const { error: createError } = await supabase
            .from('partners')
            .insert({
              name: partnerName,
              description: '',
              logo_url: url,
              tenant_id: tenantId,
              domain: domain,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          
          if (createError) throw createError;
          
          successCount++;
        } catch (error) {
          console.error(`Erreur pour ${url}:`, error);
        }
      }
      
      toast({
        title: 'Partenaires créés',
        description: `${successCount} partenaire(s) créé(s) avec succès sur ${urls.length}`,
      });
      
      // Rediriger vers la liste
      navigate('/admin/partners');
    } catch (error) {
      console.error('Erreur lors de la création multiple:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la création des partenaires.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeImage = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      // Supprimer l'image du stockage si elle existe
      if (imagePreview && imagePreview.startsWith('http')) {
        const fileName = imagePreview.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('partners')
            .remove([fileName]);
        }
      }

      // Mettre à jour l'état local
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Mettre à jour le formulaire
      const dataTransfer = new DataTransfer();
      form.setValue('logo_url', dataTransfer.files, { shouldValidate: true });
      
      // Mettre à jour la base de données si c'est une modification
      if (currentPartner?.id) {
        await supabase
          .from('partners')
          .update({ 
            logo_url: null,
            updated_at: new Date().toISOString() 
          })
          .eq('id', currentPartner.id);
      }
      
      toast({
        title: 'Image supprimée',
        description: "L'image a été supprimée avec succès.",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression de l'image:", error);
      toast({
        title: 'Erreur',
        description: "Une erreur est survenue lors de la suppression de l'image.",
        variant: 'destructive',
      });
    }
  };

  const onSubmit = async (values: PartnerFormValues) => {
    setSaving(true);
    try {
      let logoUrl = imagePreview && !imagePreview.startsWith('http') ? null : imagePreview;
      let partnerId = currentPartner?.id || '';

      // Si c'est un nouveau partenaire, on le crée d'abord avec des valeurs par défaut
      if (!currentPartner) {
        const insertData = {
          name: values.name,
          description: values.description || '',
          website_url: values.website_url || null,
          project_type: values.project_type || '',
          tenant_id: tenantId,
          domain: domain, // Enregistrer le domaine courant
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        // 🔍 DEBUG - Vérifier les données AVANT l'insertion
        console.log('🔍 PartnerEdit - Données à insérer:', insertData);
        
        const { data: newPartner, error: createError } = await supabase
          .from('partners')
          .insert(insertData)
          .select()
          .single();
        
        // 🔍 DEBUG - Vérifier les données APRÈS l'insertion
        console.log('🔍 PartnerEdit - Données insérées:', newPartner);
          
        if (createError) throw createError;
        partnerId = newPartner.id;
      }

      // Gérer l'upload du logo si présent
      if (values.logo_url && values.logo_url.length > 0 && values.logo_url[0] instanceof File) {
        const file = values.logo_url[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `partner_${partnerId}_${Date.now()}.${fileExt}`;
        const filePath = `tenant/${tenantId}/${fileName}`;
        
        // Supprimer l'ancienne image si elle existe et si elle est dans notre bucket
        if (imagePreview && imagePreview.includes('storage/v1')) {
          try {
            // Extraire le chemin du fichier à partir de l'URL
            const pathParts = imagePreview.split('media/')[1];
            if (pathParts) {
              await supabase.storage
                .from('media')
                .remove([decodeURIComponent(pathParts)]);
            }
          } catch (error) {
            console.error("Erreur lors de la suppression de l'ancien logo:", error);
          }
        }
        
        // Téléverser le nouveau fichier dans le dossier du tenant
        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        logoUrl = publicUrlData.publicUrl;
      }

      // Mettre à jour le partenaire avec les données du formulaire et l'URL du logo
      const { error: updateError } = await supabase
        .from('partners')
        .update({
          name: values.name,
          description: values.description,
          website_url: values.website_url || null,
          project_type: values.project_type || '',
          logo_url: logoUrl,
          tenant_id: tenantId, // S'assurer que tenant_id est toujours à jour
          domain: domain, // S'assurer que domain est toujours à jour
          updated_at: new Date().toISOString(),
          ...(isNewPartner ? {} : { id: currentPartner?.id })
        })
        .eq('id', partnerId);

      if (updateError) throw updateError;

      toast({
        title: isNewPartner ? 'Partenaire créé' : 'Partenaire mis à jour',
        description: `Le partenaire a été ${isNewPartner ? 'créé' : 'mis à jour'} avec succès.`,
      });

      // Rediriger vers la liste des partenaires
      navigate('/admin/partners');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du partenaire:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la sauvegarde du partenaire.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-[calc(100vh-200px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="flex items-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/partners')}
            className="mr-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-white">
            {isNewPartner ? 'Nouveau partenaire' : 'Modifier le partenaire'}
          </h1>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="text-gray-200">Nom du partenaire</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Nom du partenaire"
                              {...field}
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="text-gray-200">Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Description du partenariat ou du projet"
                              {...field}
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 min-h-[100px]"
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="project_type"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="text-gray-200">Type de projet</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Ex: Œuvre publique, Sculpture commémorative, etc."
                              {...field}
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200">Site web</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                              <Input
                                placeholder="https://exemple.com"
                                {...field}
                                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                              />
                            </div>
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm">
                            URL du site web du partenaire (optionnel)
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-8">
                <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4 text-gray-200">Logo</h3>
                    
                    <FormField
                      control={form.control}
                      name="logo_url"
                      render={({ field: { value, onChange, ...fieldProps } }) => (
                        <FormItem>
                          <FormControl>
                            <div className="space-y-4">
                              <input
                                type="file"
                                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                                ref={fileInputRef}
                                onChange={(e) => {
                                  handleImageChange(e);
                                }}
                                className="hidden"
                                id="logo-upload"
                                multiple
                              />
                              
                              {imagePreview ? (
                                <div className="relative w-full h-48 bg-white/10 rounded-lg overflow-hidden">
                                  <img
                                    src={imagePreview}
                                    alt="Logo preview"
                                    className="w-full h-full object-contain"
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    className="absolute top-2 right-2"
                                    onClick={removeImage}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div 
                                  className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center bg-white/5 hover:border-white/20 transition-colors cursor-pointer"
                                  onDrop={handleImageChange}
                                  onDragOver={handleDragOver}
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  <Building className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                                  <p className="text-sm text-gray-400 mb-2">
                                    Glissez-déposez un ou plusieurs logos
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    SVG, PNG, JPG ou WEBP (max. 5MB par fichier)
                                  </p>
                                  <p className="text-xs text-amber-400 mt-2 font-medium">
                                    💡 Sélectionnez plusieurs fichiers pour créer plusieurs partenaires
                                  </p>
                                </div>
                              )}
                              
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="border-white/10 text-white hover:bg-white/5"
                                    title="Sélectionnez un ou plusieurs fichiers (Ctrl+Clic pour sélection multiple)"
                                  >
                                    <Image className="mr-2 h-4 w-4" />
                                    Uploader (multi)
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                      setMultiSelectMode(false);
                                      setShowMediaSelector(true);
                                    }}
                                    className="bg-gray-700 hover:bg-gray-600 text-white border-0"
                                  >
                                    <Image className="mr-2 h-4 w-4" />
                                    Médiathèque (1)
                                  </Button>
                                </div>
                                
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setMultiSelectMode(true);
                                    setShowMediaSelector(true);
                                  }}
                                  className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                >
                                  <Plus className="mr-2 h-4 w-4" />
                                  Médiathèque (multi) - Créer plusieurs partenaires
                                </Button>
                              </div>
                            </div>
                          </FormControl>
                          <FormDescription className="text-gray-400 text-sm mt-2">
                            Le logo sera affiché dans la section "Ils m'ont fait confiance"
                          </FormDescription>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                <div className="flex justify-end mt-8">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Enregistrer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Form>
        
        {/* Sélecteur de média */}
        <MediaSelector
          isOpen={showMediaSelector}
          onClose={() => {
            setShowMediaSelector(false);
            setMultiSelectMode(false);
          }}
          onSelect={multiSelectMode ? handleMultipleMediaSelection : (url) => {
            setImagePreview(url as string);
            setShowMediaSelector(false);
          }}
          selectedUrl={!multiSelectMode ? (imagePreview || undefined) : undefined}
          title={multiSelectMode ? "Sélectionner plusieurs logos" : "Sélectionner un logo"}
          multiSelect={multiSelectMode}
        />
      </div>
    </AdminLayout>
  );
};

export default PartnerEdit;
